import {
	DeleteOutlined,
	ExclamationCircleOutlined,
	LoadingOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { Button, Modal, Upload } from 'antd';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { companySetup } from 'Api/companySetup';
import React, { useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import styles from './index.module.scss';

const ImageUpload: React.FC = () => {
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewImage, setPreviewImage] = useState<string>('');
	const [previewTitle, setPreviewTitle] = useState('');
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [logoLoading, setLogoLoading] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);

	useEffect(() => {
		fetchCompanyLogo();
	}, []);

	const fetchCompanyLogo = async () => {
		try {
			setLogoLoading(true);
			const response = await companySetup.getCompanyLogo();

			if (response.status === 200) {
				const blob = new Blob([response.data], {
					type: response.headers['content-type'],
				});
				const imageUrl = URL.createObjectURL(blob);

				// Set the file list with the fetched image
				setFileList([
					{
						uid: '-1',
						name: 'Company Logo',
						status: 'done',
						url: imageUrl,
					},
				]);
			}
		} catch (error:any) {
		const errorMessage = error.message;
			// toastText('Failed to fetch company logo', 'error');
		} finally {
			setLogoLoading(false);
		}
	};

	const handlePreview = async (file: UploadFile) => {
		if (!file.url && !file.preview) {
			file.preview = await getBase64(file.originFileObj as RcFile);
		}

		setPreviewImage(file.url || (file.preview as string));
		setPreviewVisible(true);
		setPreviewTitle(
			file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1)
		);
	};

	const handleChange = ({
		fileList: newFileList,
	}: {
		fileList: UploadFile[];
	}) => {
		setFileList(newFileList);
	};

	const handleCancel = () => setPreviewVisible(false);

	const beforeUpload = (file: RcFile) => {
		const isImage = file.type.startsWith('image/');
		const isLt5M = file.size / 1024 / 1024 < 5; // 5MB limit

		if (!isImage) {
			toastText('You can only upload image files!', 'error');
			return Upload.LIST_IGNORE;
		}

		if (!isLt5M) {
			toastText('Image must be smaller than 5MB!', 'error');
			return Upload.LIST_IGNORE;
		}

		return false; // Prevent auto upload if using manual upload process
	};

	const uploadToBackend = async () => {
		if (fileList.length === 0 || fileList[0].originFileObj === undefined) {
			toastText('Company already has a logo', 'error');
			return;
		}

		const formData = new FormData();
		formData.append('moduleName', 'companyLogo');
		formData.append('file', fileList[0].originFileObj as Blob);

		try {
			setButtonLoading(true);
			await companySetup.uploadImage(formData);
			toastText('File uploaded successfully', 'success');
			await fetchCompanyLogo(); // Refresh the logo after successful upload
		} catch (error: any) {
			let message =
				error?.response?.data?.message || `Failed to upload image`;
			toastText(message, 'error');
		} finally {
			setButtonLoading(false);
		}
	};

	const deleteCompanyLogo = async () => {
		try {
			setDeleteLoading(true);
			await companySetup.deleteCompanyLogo();

			// Clear the file list and revoke object URLs to free memory
			fileList.forEach((file) => {
				if (file.url && file.url.startsWith('blob:')) {
					URL.revokeObjectURL(file.url);
				}
			});

			setFileList([]);
			toastText('Logo deleted successfully', 'success');
			return true;
		} catch (error: any) {
			let message =
				error?.message ||
				error?.response?.data?.message ||
				'Failed to delete logo';
			toastText(message, 'error');
			return false;
		} finally {
			setDeleteLoading(false);
		}
	};

	const getBase64 = (file: RcFile): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});

	const uploadButton = (
		<div>
			{logoLoading ? <LoadingOutlined /> : <PlusOutlined />}
			<div style={{ marginTop: 8 }}>Upload</div>
		</div>
	);

	return (
		<div className={styles['upload-container']}>
			<div className={styles['horizontal-align']}>
				<Upload
					listType="picture-circle"
					fileList={fileList}
					onPreview={handlePreview}
					onChange={handleChange}
					beforeUpload={beforeUpload}
					accept="image/*"
					onRemove={() => {
						// This will trigger when user clicks the remove icon on the upload item
						return new Promise((resolve) => {
							Modal.confirm({
								title: 'Remove Logo',
								icon: (
									<ExclamationCircleOutlined
										style={{ color: 'red' }}
									/>
								),
								content:
									'Are you sure you want to remove this logo?',
								okText: 'Confirm',
								cancelText: 'Cancel',
								okType: 'primary',
								okButtonProps: {
									style: {
										backgroundColor: '#584495',
										borderColor: '#584495',
									},
								},
								cancelButtonProps: {
									style: {
										color: '#666',
										borderColor: '#d9d9d9',
										backgroundColor: '#fff',
									},
								},
								onOk: async () => {
									try {
										const success =
											await deleteCompanyLogo();
										resolve(success);
									} catch (error) {
										resolve(false);
									}
								},
								onCancel: () => {
									resolve(false);
								},
							});
						});
					}}
					showUploadList={{
						showPreviewIcon: true,
						showRemoveIcon: fileList.length > 0,
						removeIcon: deleteLoading ? (
							<LoadingOutlined />
						) : (
							<DeleteOutlined />
						),
					}}
				>
					{fileList.length >= 1 ? null : uploadButton}
				</Upload>

				<Button
					type="primary"
					onClick={uploadToBackend}
					disabled={fileList.length === 0 || buttonLoading}
					style={{ marginTop: 16 }}
				>
					Upload
					{buttonLoading ? <LoadingOutlined /> : ''}
				</Button>
			</div>

			<Modal
				open={previewVisible}
				title={previewTitle}
				footer={null}
				onCancel={handleCancel}
				style={{ top: '0px' }}
				closable={false}
			>
				<img
					alt="preview"
					style={{ width: '100%' }}
					src={previewImage}
				/>
			</Modal>
		</div>
	);
};

export default ImageUpload;
